import { useTranslator } from '@Localization';
import { useInjection } from 'inversify-react';
import { checkAll, checkUserPlan, checkWorkspaceRole, IPermissionCheckResult } from '../../../modules/permissions/permissionsHelpers';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { Plan } from '../user/user.store';
import { WorkspaceRole } from '../workspaces/workspaces.store';
import { AnnotateStage, FreeAccessMode } from './freeAccess.store';

interface IFreeAccessPermissions {
  canUseKeyboardShortcuts(): IPermissionCheckResult;
  canSubmit(mode: FreeAccessMode): IPermissionCheckResult;
  canSave(mode: FreeAccessMode): IPermissionCheckResult;
  canDiscard(stage: AnnotateStage): IPermissionCheckResult;
  canSaveDraft(stage: AnnotateStage, mode: FreeAccessMode): IPermissionCheckResult;
  canReAnnotate(): IPermissionCheckResult;
  canChangeMode(): IPermissionCheckResult;
  canEditAnnotation(): IPermissionCheckResult;
  canSeeImageName(): IPermissionCheckResult;
}

export const useFreeAccessPermissions = (): IFreeAccessPermissions => {
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const currentWorkspace = currentWorkspaceStore.currentWorkspace;
  const { t } = useTranslator();

  const canUseKeyboardShortcuts = (): IPermissionCheckResult => {
    return checkWorkspaceRole(
      [
        WorkspaceRole.Assistant,
        WorkspaceRole.Collaborator,
        WorkspaceRole.Developer,
        WorkspaceRole.Manager,
        WorkspaceRole.Owner,
        WorkspaceRole.Trainer,
        WorkspaceRole.Worker,
      ],
      currentWorkspace,
      t
    );
  };

  const canSubmit = (mode: FreeAccessMode): IPermissionCheckResult => {
    const result = checkUserPlan([Plan.Annotation, Plan.Training, Plan.Admin, Plan.Free], currentWorkspace, t);

    return { ...result, allowed: (mode === FreeAccessMode.ANNOTATION || mode === FreeAccessMode.CORRECT) && result.allowed };
  };

  const canSave = (mode: FreeAccessMode): IPermissionCheckResult => {
    const result = checkUserPlan([Plan.Annotation, Plan.Training, Plan.Admin, Plan.Free], currentWorkspace, t);

    return { ...result, allowed: (mode === FreeAccessMode.ANNOTATIONRESET || mode === FreeAccessMode.CORRECT) && result.allowed };
  };

  const canDiscard = (stage: AnnotateStage): IPermissionCheckResult => {
    const result = checkAll([Plan.Annotation, Plan.Training, Plan.Admin, Plan.Free], [WorkspaceRole.Owner, WorkspaceRole.Manager], currentWorkspace, t);

    return { ...result, allowed: stage !== AnnotateStage.NOTANNOTATED && result.allowed };
  };

  const canSaveDraft = (stage: AnnotateStage, mode: FreeAccessMode): IPermissionCheckResult => {
    const result = checkUserPlan([Plan.Annotation, Plan.Training, Plan.Admin, Plan.Free], currentWorkspace, t);

    return {
      allowed:
        !(
          stage === AnnotateStage.ANNOTATED ||
          stage === AnnotateStage.REVIEWED ||
          stage === AnnotateStage.REJECTED ||
          mode === FreeAccessMode.ANNOTATIONRESET
        ) && result.allowed,
    };
  };

  const canReAnnotate = (): IPermissionCheckResult => {
    return checkAll(
      [Plan.Annotation, Plan.Training, Plan.Admin, Plan.Free],
      [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer, WorkspaceRole.Collaborator, WorkspaceRole.Assistant],
      currentWorkspace,
      t
    );
  };

  const canEditAnnotation = (): IPermissionCheckResult => {
    return checkAll(
      [Plan.Annotation, Plan.Training, Plan.Admin, Plan.Free],
      [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer, WorkspaceRole.Collaborator, WorkspaceRole.Assistant],
      currentWorkspace,
      t
    );
  };

  const canChangeMode = (): IPermissionCheckResult => {
    return checkAll(
      [Plan.Annotation, Plan.Training, Plan.Admin, Plan.Free],
      [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer, WorkspaceRole.Collaborator, WorkspaceRole.Assistant],
      currentWorkspace,
      t
    );
  };

  const canSeeImageName = (): IPermissionCheckResult => {
    return checkWorkspaceRole(
      [
        WorkspaceRole.Owner,
        WorkspaceRole.Manager,
        WorkspaceRole.Developer,
        WorkspaceRole.Trainer,
        WorkspaceRole.Collaborator,
        WorkspaceRole.Guest,
        WorkspaceRole.Assistant,
      ],
      currentWorkspace,
      t
    );
  };

  return {
    canUseKeyboardShortcuts,
    canSubmit,
    canSave,
    canDiscard,
    canSaveDraft,
    canReAnnotate,
    canChangeMode,
    canEditAnnotation,
    canSeeImageName,
  };
};
